<!-- =========================================================================================
  File Name: DashboardAnalytics.vue
  Description: Dashboard Analytics

========================================================================================== -->

<template>
  <div class="vx-row">
    <div class="vx-col w-full mb-base">
      <vx-card>
        <form-wizard :subtitle="null" :title="null" color="rgba(var(--vs-primary), 1)" errorColor="rgba(var(--vs-danger), 1)">
          <tab-content :before-change="validateStep1" class="mb-5" icon="feather icon-upload" title="Upload Users">

            <!-- tab 1 content -->
            <vx-card class="d-theme-dark-light-bg mt-1">
              <div class="vx-row items-center rounded">
                <div class="vx-col md:w-1/2 w-full p-10">
                  <h1 class="my-3">Upload Users</h1>
                  <p>Use this template to fill in the details to mass upload users to Intellectual Point
                    Simulations.<br>Use Microsoft Excel or Google Sheets to edit the template</p>
                  <vs-button class="mt-4 font-bold " color="primary"
                             href="https://cdn.discordapp.com/attachments/746242308084858900/864346687744639006/Simulations_User_Upload_Template.xlsx"
                             icon="icon-download" icon-pack="feather" type="border">Download Template
                  </vs-button>
                </div>
                <div class="vx-col md:w-1/2 w-full p-10">
                  <upload-file dropTitle="Drop Users File or" allowedFileTypes=".xlsx"
                               :onSuccess="this.readerData"></upload-file>
                  <h6 v-if="excelData.success === false && excelData.errorMsg" class="mt-2 text-danger font-semibold">
                    {{ excelData.errorMsg }}</h6>
                  <h6 v-if="excelData.success === true && excelData.results" class="mt-2 text-success font-semibold">
                    Uploaded File Contains {{ excelData.results.length }} Valid Users</h6>
                </div>
              </div>
            </vx-card>
          </tab-content>
          <tab-content :before-change="validateStep2" class="mb-5" icon="feather icon-edit" title="Edit Users">
            <vs-table :data="excelData.results" v-if="excelData.results && excelData.results" max-items="10" pagination
                      search :key="excelTableKey">
              <template slot="header">
                <h1>Uploaded Users</h1>
              </template>

              <template slot="thead">
                <vs-th>Name</vs-th>
                <vs-th>Username</vs-th>
                <vs-th>Email</vs-th>
                <vs-th>Password</vs-th>
                <vs-th>Action</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr v-for="(attach, index) in data" :key="attach['Username']">

                  <vs-td :data="`${attach['First Name']} ${attach['Last Name']}`">
                    {{ `${attach['First Name']} ${attach['Last Name']}` }}
                  </vs-td>

                  <vs-td :data="`${attach['Email']}`">
                    {{ `${attach['Email']}` }}
                  </vs-td>

                  <vs-td :data="attach['Username']">
                    <div class="flex align-middle">
                      <p class="mr-1">{{ attach['Username'] }}</p>
                      <vx-tooltip color="warning" text="Username Must End With @ip.com"
                                  v-if="!attach['Username'].endsWith('@ip.com')">
                        <feather-icon icon="AlertTriangleIcon" svgClasses="h-5 w-5 ml-1 stroke-current text-warning"/>
                      </vx-tooltip>
                    </div>
                  </vs-td>

                  <vs-td :data="attach['Password']">
                    {{ attach['Password'] }}
                  </vs-td>

                  <vs-td>
                    <vs-button color="primary" size="small" type="border" icon-pack="feather" icon="icon-edit"
                               @click="editUserStart(attach, index)">
                      Edit
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </tab-content>
          <tab-content :before-change="validateStep3" class="mb-5" icon="feather icon-book" title="Assign Courses">
            <vs-table :data="courseSearchResults" :sst="true" max-items="5" search @search="courseSearch" class="mb-2">
              <template slot="header">
                <h3>Search Courses</h3>
              </template>

              <template slot="thead">
                <vs-th>Id</vs-th>
                <vs-th>Name</vs-th>
                <vs-th>Action</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr v-for="(attach) in data" :key="attach.id">

                  <vs-td :data="attach.id">
                    {{ attach.id }}
                  </vs-td>

                  <vs-td :data="`${attach.name}`">
                    <div class="flex align-middle items-center">
                      <img :src="attach.thumbnail" class="rounded mr-3" width="40px">
                      {{ `${attach.name}` }}
                    </div>
                  </vs-td>

                  <vs-td>
                    <vs-button color="primary" size="small" type="border" @click="assignCourse(attach)">
                      Assign
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
            <vs-divider/>
            <vs-table :data="courseAssignSelected" max-items="5" pagination class="mt-2" :key="courseAssignTableKey">
              <template slot="header">
                <h3 class="mb-5">Assigned Courses</h3>
              </template>

              <template slot="thead">
                <vs-th>Id</vs-th>
                <vs-th>Name</vs-th>
                <vs-th>Start</vs-th>
                <vs-th>Expiry</vs-th>
                <vs-th>Action</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr v-for="(attach, index) in data" :key="attach.id">

                  <vs-td :data="attach.id">
                    {{ attach.id }}
                  </vs-td>

                  <vs-td :data="`${attach.name}`">
                    <div class="flex align-middle items-center">
                      <img :src="attach.thumbnail" class="rounded mr-3" width="40px">
                      {{ `${attach.name}` }}
                    </div>
                  </vs-td>

                  <vs-td :data="attach.starting_date" v-if="attach.starting_date">
                    {{
                      (new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })).format(attach.starting_date)
                    }}
                  </vs-td>

                  <vs-td v-else>
                    Immediately
                  </vs-td>


                  <vs-td :data="attach.ending_date" v-if="attach.ending_date">
                    {{
                      (new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })).format(attach.ending_date)
                    }}
                  </vs-td>

                  <vs-td :data="attach.calculate_end_date" v-if="attach.calculate_end_date">
                    {{ attach.calculate_end_date }}
                  </vs-td>

                  <vs-td>
                    <vs-button color="primary" size="small" type="border"
                               @click="courseAssignSelected.splice(index, 1)">
                      Remove
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </tab-content>
          <tab-content :before-change="validateStep4" class="mb-5" icon="feather icon-eye" title="Review Details">
            <div class="vx-row" v-if="">
              <div class="vx-col w-1/2">
                <h1>{{ goodToSubmit ? 'Ready To Create Users, Press Next To Create' : 'Fix Errors Before Re-Submitting' }}</h1>
                <h3 class="mt-2">Total Users : {{  excelData.results.length }}</h3>
                <h3 class="mt-2">Total Courses : {{ courseAssignSelected.length }}</h3>
                <h3 class="mt-2" v-if="goodToSubmitErrors.length !== 0">Total Errors : {{ goodToSubmitErrors.length }}</h3>
                <vs-button class="mt-4" @click="validateStep3" v-if="!goodToSubmit">Re-Check For Errors</vs-button>
              </div>
              <div class="vx-col w-1/2 p-12" style="background-color: #161719" v-if="goodToSubmitErrors.length !==0">
                <div style="max-height: 650px; overflow-y: scroll;">
                  <vue-json-pretty :data="goodToSubmitErrors"/>
                </div>
              </div>
            </div>
          </tab-content>
          <tab-content class="mb-5" icon="feather icon-check" title="Complete">
            <div class="vx-row" v-if="">
              <div class="vx-col w-1/2 p-2">
                <h5>Total Warnings : {{ submittedWarnings.length }}</h5>
                <div style="background-color: #161719" class="p-12 mt-3">
                  <div style="max-height: 650px; overflow-y: scroll;" v-if="submittedWarnings.length !==0">
                    <vue-json-pretty :data="submittedWarnings"/>
                  </div>
                  <h1 v-else>No Warnings</h1>
                </div>

              </div>
              <div class="vx-col w-1/2 p-2">
                <h5>Total Warnings : {{ submittedErrors.length }}</h5>
                <div style="background-color: #161719" class="p-12 mt-3">
                  <div style="max-height: 650px; overflow-y: scroll;" v-if="submittedErrors.length !==0">
                    <vue-json-pretty :data="submittedErrors"/>
                  </div>
                  <h1 v-else>No Errors</h1>
                </div>
              </div>
            </div>
            <div class="vx-col w-full mt-3">
              <h1>Successful Creations : {{ submittedSuccess }}</h1>
              <h1>Upload Count : {{ excelData.results.length }}</h1>

            </div>
          </tab-content>
        </form-wizard>
      </vx-card>
    </div>
    <vs-popup :active.sync="editSingleUserActive" title="Edit User Values" id="EditUserPopup">
      <vs-input v-model="editSingleUser.first_name" class="mb-8 w-full" label-placeholder="First Name"/>
      <vs-input v-model="editSingleUser.last_name" class="mb-8 w-full" label-placeholder="Last Name"/>
      <vs-input v-model="editSingleUser.username" class="mb-8 w-full" v-validate="'required|min:3'" label-placeholder="Username"/>
      <vs-input v-model="editSingleUser.password" class="mb-6 w-full" label-placeholder="Password"/>
      <vs-button color="success" type="filled" @click="editUserSave">Save Changes</vs-button>
    </vs-popup>
    <vs-popup :active.sync="courseAssignPopup" :title="`Assign Course`">
      <vx-card>
      </vx-card>
      <div class="mt-6 mb-4">
        <h6>Choose Subscription Expiry</h6>
        <datepicker :inline="true" placeholder="Select Date" v-model="courseAssignCalendar" class="datepicker-user-popup mt-4"></datepicker>
        <ul class="flex mt-5">
          <li>
            <vs-radio v-model="courseAssignDuration" @input="calculateCalendarDate" vs-value="1" class="mr-4">1 Month</vs-radio>
          </li>
          <li>
            <vs-radio v-model="courseAssignDuration" @input="calculateCalendarDate" vs-value="3" class="mr-4">3 Months</vs-radio>
          </li>
          <li>
            <vs-radio v-model="courseAssignDuration" @input="calculateCalendarDate" vs-value="6" class="mr-4">6 Months</vs-radio>
          </li>
          <li>
            <vs-radio v-model="courseAssignDuration" @input="calculateCalendarDate" vs-value="12" class="mr-4">1 Year</vs-radio>
          </li>
        </ul>
      </div>

      <p class="text-danger font-bold " v-if="courseAssignCalendar === null">You Must Choose A Subscription Duration</p>

      <vs-button color="primary" type="filled"
                 @click="assignCourseConfirm"
                 v-if="courseAssignCalendar">Assign Course
      </vs-button>

    </vs-popup>
  </div>
</template>

<script>
import {FormWizard, TabContent} from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import UploadFile from '@/components/upload/UploadFile'
import XLSX from 'xlsx'
import Datepicker from 'vuejs-datepicker'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'

export default {
  data () {
    return {
      excelTableKey: 0,
      excelData: {
        success: false,
        errorMsg: '',
        header: null,
        results: [],
        meta: null
      },

      editSingleUserActive: false,
      editSingleUser: {
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        password: '',
        index: null
      },

      courseSearchResults: [],
      courseAssignTableKey: 0,
      courseAssignSelected: [],

      groupSearchResults: [],
      groupAssignTableKey: 0,
      groupAssignSelected: [],

      courseAssignPopup: false,
      courseAssignData: {},
      courseAssignDuration: null,

      courseAssignCalendar: null,
      subscriptionsCreate: [],

      goodToSubmit: false,
      goodToSubmitErrors: [],

      submittedUsers: false,
      submittedWarnings: [],
      submittedErrors: [],
      submittedSuccess: 0


    }
  },
  methods: {
    assignCourseConfirm () {
      const pl = this.courseAssignData

      pl.ending_date = this.courseAssignCalendar.getTime()

      this.courseAssignSelected.push(pl)
      this.courseAssignData = {}
      this.courseAssignPopup = false
      this.courseAssignDuration = null
      this.courseAssignCalendar = null
    },
    assignCourse (course) {

      let addCourse = true
      this.courseAssignSelected.forEach(item => {
        if (item.id === course.id) {
          addCourse = false
          return false
        }
      })

      if (addCourse === true) {
        this.courseAssignData = course
        this.courseAssignPopup = true
      } else {
        this.$vs.notify({
          title: 'Course Already Assigned',
          text: 'The course you tried to assign has already been assigned',
          color: 'danger',
          iconPack: 'feather',
          position: 'top-right',
          icon: 'icon-alert-circle'
        })
      }
    },
    calculateCalendarDate () {
      const date = new Date()
      const months = parseInt(this.courseAssignDuration)
      date.setMonth(date.getMonth() + months)
      this.courseAssignCalendar = date
    },
    courseSearch (term) {
      this.$http.get(`courses?search=${term}&amount=5`)
        .then(response => {
          if (response.data) {
            this.courseSearchResults = response.data.data
          }
        })
    },
    groupSearch (term) {
      this.$http.get(`groups?search=${term}&amount=5`)
        .then(response => {
          if (response.data) {
            this.groupSearchResults = response.data.data
          }
        })
    },
    editUserSave () {
      this.excelData.results.splice(this.editSingleUser.index, 1)

      this.excelData.results.unshift({
        'First Name' : this.editSingleUser.first_name,
        'Last Name' : this.editSingleUser.last_name,
        'Username' : this.editSingleUser.username.toLowerCase(),
        'Password' : this.editSingleUser.password
      })

      this.editSingleUserActive = false
      this.excelTableKey++
    },
    editUserStart (user, index) {
      this.editSingleUser = {
        first_name: user['First Name'],
        last_name: user['Last Name'],
        username: user['Username'],
        password: user['Password'],
        index
      }
      this.editSingleUserActive = true
    },
    excelError (msg) {
      this.excelData.success = false
      this.excelData.errorMsg = msg

      this.$vs.notify({
        title: 'Sheet Parsing Error',
        text: msg,
        color: 'danger',
        iconPack: 'feather',
        position: 'top-right',
        icon: 'icon-alert-circle'
      })
    },
    excelSuccess (msg) {
      this.excelData.success = true

      this.$vs.notify({
        title: 'File Uploaded Successfully',
        text: msg,
        color: 'success',
        iconPack: 'feather',
        position: 'top-right',
        icon: 'icon-check-circle'
      })
    },
    getHeaderRow (sheet) {
      const headers = []
      const range = XLSX.utils.decode_range(sheet['!ref'])
      let C = undefined
      const R = range.s.r
      /* start in the first row */
      for (C = range.s.c; C <= range.e.c; ++C) { /* walk every column in the range */
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })]
        /* find the cell in the first row */
        let hdr = `UNKNOWN ${  C}` // <-- replace with your desired default
        if (cell && cell.t) hdr = XLSX.utils.format_cell(cell)
        headers.push(hdr)
      }
      return headers
    },
    generateData ({ header, results, meta }) {

      if (!header.includes('First Name')) {
        return this.excelError('Missing "First Name" Column, Please Use The Template File.')
      }

      if (!header.includes('Last Name')) {
        return this.excelError('Missing "Last Name" Column, Please Use The Template File.')
      }

      if (!header.includes('Username')) {
        return this.excelError('Missing "Username" Column, Please Use The Template File.')
      }

      if (results.length <= 0) {
        return this.excelError('File Is Empty, Please Fill Out Values Before Uploading.')
      }

      this.excelSuccess('File Uploaded & Parsed Successfully')
      this.excelData.header = header
      this.excelData.results = results
      this.excelData.meta = meta
      console.log(this.excelData)
    },
    fixUsernames () {
      let fixedUsernames = false
      this.excelData.results.forEach((item, index) => {
        item['Username'] = item['Username'].toLowerCase()
        item['Password'] = 'student12345'
        if (!item['Username'].endsWith('@ip.com')) {
          fixedUsernames = true
          item['Username'] = `${item['Username']  }@ip.com`
        }
        this.excelData.results[index] = item
      })
      if (fixedUsernames) {
        this.$vs.notify({
          title: 'Syntax Automatically Fixed',
          text: 'Some errors that have been found in student usernames have been fixed',
          color: 'success',
          iconPack: 'feather',
          position: 'top-right',
          icon: 'icon-check-circle'
        })
      }
    },
    readerData (rawFile) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const header = this.getHeaderRow(worksheet)
          const results = XLSX.utils.sheet_to_json(worksheet)
          const meta = { sheetName: firstSheetName }
          this.generateData({ header, results, meta })
          resolve()
        }
        reader.readAsArrayBuffer(rawFile)
      })
    },
    validateStep1 () {
      return new Promise((resolve, reject) => {
        if (this.excelData.success === true) {
          this.fixUsernames()
          resolve(true)
        } else {
          this.$vs.notify({
            title: 'No File Uploaded',
            text: 'You must upload a file before continuing',
            color: 'danger',
            iconPack: 'feather',
            position: 'top-right',
            icon: 'icon-alert-circle'
          })
          reject('correct all values')
        }
      })
    },
    validateStep2 () {
      return new Promise((resolve, reject) => {
        resolve(true)
      })
    },
    validateStep3 () {
      return new Promise((resolve, reject) => {

        this.courseAssignSelected.forEach(item => {

          const subscription = {
            'course_id': item.id,
            'ending_date' : item.ending_date
          }
          if (item.starting_date) {
            subscription.starting_date = item.starting_date
          }

          this.subscriptionsCreate.push(subscription)
        })

        const pl = {
          users: this.excelData.results,
          subscriptions: this.subscriptionsCreate
        }

        this.$vs.loading()

        this.$http.post('auth/mass/validate', pl)
          .then(response => {

            if (response.status === 200) {
              this.$vs.loading.close()

              this.goodToSubmit = response.data.data.proceed
              this.goodToSubmitErrors = response.data.data.errors


            }
          })
          .catch(exception => {
            this.$vs.loading.close()
            this.goodToSubmit = false

            this.goodToSubmitErrors.push({
              code: 'FAILURE',
              error: exception.response.data.error.description
            })

            return this.$vs.notify({
              title: 'Errors With Submission',
              text: 'Please Check The Log For Details',
              color: 'danger',
              position: 'top-right'
            })
          })

        resolve(true)
      })
    },
    validateStep4 () {
      return new Promise((resolve, reject) => {

        if (this.goodToSubmit !== true) {
          this.$vs.notify({
            title: 'Outstanding Errors',
            text: 'Please resolve all errors before submitting',
            color: 'danger',
            position: 'top-right'
          })
          return reject('errors')
        }


        if (this.submittedUsers !== false) {
          this.$vs.notify({
            title: 'Already Submitted Users',
            text: 'Please refresh the page to start again.',
            color: 'danger',
            position: 'top-right'
          })
          return reject('errors')
        }

        const pl = {
          users: this.excelData.results,
          subscriptions: this.subscriptionsCreate
        }

        this.$vs.loading()

        this.$http.post('auth/mass', pl)
          .then(response => {

            if (response.status === 200) {
              this.$vs.loading.close()

              this.submittedErrors = response.data.data.errors
              this.submittedWarnings = response.data.data.warnings
              this.submittedSuccess = response.data.data.success

            }
          })
          .catch(exception => {
            this.$vs.loading.close()
            this.goodToSubmit = false

            this.goodToSubmitErrors.push({
              code: 'FAILURE',
              error: exception.response.data.error.description
            })

            return this.$vs.notify({
              title: 'Errors With Submission',
              text: 'No Users Have Been Created',
              color: 'danger',
              position: 'top-right'
            })
          })

        resolve(true)

      })
    }
  },
  mounted () {
    this.courseSearch('')
    this.groupSearch('')
  },
  components: {
    UploadFile,
    FormWizard,
    TabContent,
    XLSX,
    Datepicker,
    VueJsonPretty
  }
}
</script>
<style lang="scss">

.theme-midnight {
  #EditUserPopup {
    input.vs-inputx.vs-input--input {
      background: #050056 !important;
      border: 1px solid #ffffff !important;

      &:focus {
        border: 1px solid rgba(var(--vs-primary), 1) !important;
      }
    }
  }
}

.theme-dark {
  #EditUserPopup {
    input.vs-inputx.vs-input--input {
      background: #1B1E22 !important;
      border: 1px solid #ffffff !important;

      &:focus {
        border: 1px solid rgba(var(--vs-primary), 1) !important;
      }
    }
  }
}


.datepicker-user-popup {
  .vdp-datepicker__calendar {
    width: 100%;
    padding: 5px 0 10px 0;
  }


  span.cell.day {
    border-radius: 5%;
    margin-bottom: 0.5%;
    margin-top: 0.5%;
  }

  span.cell.day.today {
    border-radius: 5%;
    color: #fff;
    background: rgba(var(--vs-primary), 0.1);

    &:hover {
      background: rgba(var(--vs-primary), 0.2);
    }
  }
}
</style>
